import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core'
import {Location} from '@angular/common'
import {ActivatedRoute, Router} from '@angular/router'
import {FormBuilder, FormGroup, Validators} from '@angular/forms'
import {
  Observable,
  Subject,
  of,
  combineLatest,
  BehaviorSubject,
  Subscription,
} from 'rxjs'
import {
  distinctUntilChanged,
  takeUntil,
  debounceTime,
  tap,
  map,
  startWith,
  delay,
  share,
} from 'rxjs/operators'
import {SearchFilter} from '@app/core/services/search/search-filter.interface'
import moment from 'moment'
import {ContextService} from '@app/core/services/context.service'
import {ResetFilterService} from '@app/core/services/reset-filter-service'
import {TransactionFilterService} from '@app/features/terminal-park/transactions/transactions-filter.service'

export interface FilterOptions {
  filterFields: FilterField[]
  sortFields?: (SortField | string)[]
  filters?: object
  sort?: Sort | string
}

export interface FilterField {
  label: string
  field: string
  filterTypes: filterTypeValue[]
  type?: FilterFieldType | string
}

export interface SortField {
  label: string
  field: string
  asc?: boolean
}

export interface FilterFieldType {
  type: string
  typeahead?: (val) => Observable<string[]>
}

export class EnumFilterField implements FilterFieldType {
  private values: string[]

  type = 'enum'
  typeahead = (typed) => of(this.values.filter((val) => val.startsWith(typed)))

  constructor(...values: string[]) {
    this.values = values
  }
}

export class BooleanFilterField extends EnumFilterField {
  constructor() {
    super('true', 'false')
  }
}
export interface FilterQuery extends SearchFilter {}

interface Filter {
  field: FilterField
  value: string
  filterType?: filterTypeValue
  custom?: boolean
  notRemovable?: boolean // inversion for backwards compatibility
}

interface Sort {
  field: FilterField | string
  asc?: boolean
}

export enum filterTypeValue {
  BOOLEAN = 'boolean',
  POSITIVE_MATCH = 'positiveMatch',
  NEGATIVE_MATCH = 'negativeMatch',
  POSITIVE_EXACT_MATCH = 'positiveExactMatch',
  NEGATIVE_EXACT_MATCH = 'negativeExactMatch',
  BEFORE = 'before',
  AFTER = 'after',
  BETWEEN_TIMESTAMPS = 'between_timestamps',
  SINCE = 'since',
  PRIOR_TO = 'prior_to',
  LESS_THAN = 'less_than',
  GREATER_THAN = 'greater_than',
  BETWEEN_VALUES = 'between_values',
}

export const basicValueFilterTypes = [
  filterTypeValue.POSITIVE_MATCH,
  filterTypeValue.NEGATIVE_MATCH,
  filterTypeValue.POSITIVE_EXACT_MATCH,
  filterTypeValue.NEGATIVE_EXACT_MATCH,
]

export const timeFilterTypes = [
  filterTypeValue.BEFORE,
  filterTypeValue.AFTER,
  filterTypeValue.BETWEEN_TIMESTAMPS,
]
export const sinceTypes = [filterTypeValue.PRIOR_TO, filterTypeValue.SINCE]

export const ordinalFilterTypes = [
  filterTypeValue.LESS_THAN,
  filterTypeValue.GREATER_THAN,
  filterTypeValue.BETWEEN_VALUES,
]

export {SearchFilter}

@Component({
  selector: 'search-filter',
  templateUrl: './search-filter.component.html',
  styleUrls: ['./search-filter.component.scss'],
  standalone: false,
})
export class SearchFilterComponent implements OnInit, OnDestroy {
  @Input() options: FilterOptions
  @Input() showFilterSearchField = true

  @Input() total: Observable<number>
  @Input() checked: any
  @Input() isSelectable: boolean = false
  @Input() visibleItems: Observable<number>
  @Input() transactionFilter = false
  @Output() filterChange: EventEmitter<FilterQuery> =
    new EventEmitter<FilterQuery>()

  basicValueFilterTypes = basicValueFilterTypes
  timeFilterTypes = timeFilterTypes
  ordinalFilterTypes = ordinalFilterTypes
  sinceTypes = sinceTypes
  hideSearch = true
  activeFilterType$ = new BehaviorSubject<filterTypeValue>(null)
  isAdvanced = false
  sortField
  sortAscending = true
  sortFieldChanged = false
  filters: Filter[] = []
  filterTypes$: Observable<
    {value: filterTypeValue; label: string; disabled: boolean}[]
  >
  listeningToFilterChanges = true
  hasInvalidInput = false
  invalidInputString = ''
  defaultFilterTypes$ = of([
    {
      value: filterTypeValue.POSITIVE_MATCH,
      label: 'matches',
      disabled: true,
    },
    {
      value: filterTypeValue.NEGATIVE_MATCH,
      label: 'not matches',
      disabled: true,
    },
    {
      value: filterTypeValue.POSITIVE_EXACT_MATCH,
      label: 'exactly',
      disabled: true,
    },
    {
      value: filterTypeValue.NEGATIVE_EXACT_MATCH,
      label: 'exactly not',
      disabled: true,
    },
    {
      value: filterTypeValue.GREATER_THAN,
      label: 'more than',
      disabled: true,
    },
    {
      value: filterTypeValue.LESS_THAN,
      label: 'less than',
      disabled: true,
    },
    {
      value: filterTypeValue.BETWEEN_VALUES,
      label: 'between',
      disabled: true,
    },
    {
      value: filterTypeValue.PRIOR_TO,
      label: 'before',
      disabled: true,
    },
    {
      value: filterTypeValue.SINCE,
      label: 'since',
      disabled: true,
    },
    {
      value: filterTypeValue.AFTER,
      label: 'after time',
      disabled: true,
    },
    {
      value: filterTypeValue.BEFORE,
      label: 'before time',
      disabled: true,
    },
    {
      value: filterTypeValue.BETWEEN_TIMESTAMPS,
      label: 'between',
      disabled: true,
    },
    {
      value: filterTypeValue.BOOLEAN,
      label: 'on',
      disabled: true,
    },
  ])
  filterType = filterTypeValue.POSITIVE_MATCH
  searchValue
  query: string
  routeBase: string
  routeQuery: any
  routeParams = false
  form: FormGroup
  advancedForm: FormGroup
  private filterSubscription: Subscription

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    protected contextSvc: ContextService,
    private resetFilterService: ResetFilterService,
    private cdr: ChangeDetectorRef,
    private filterService: TransactionFilterService,
    private router: Router,
    private location: Location,
  ) {
    this.advancedForm = this.fb.group({
      advanced: this.fb.control(null, [
        Validators.minLength(1),
        Validators.required,
      ]),
    })
  }

  ngOnInit() {
    // discover the base url for this route
    const routeBase = this.location.path(true)
    const queryIndex = routeBase.indexOf('?')
    this.routeBase =
      queryIndex >= 0 ? routeBase.substr(0, queryIndex) : routeBase

    // subscribe to any query param changes
    this.route.queryParams
      .pipe(
        // skip(1),
        takeUntil(this.end$),
      )
      .subscribe((params) => {
        // parse the search fields
        this.parseRouteParams(params)
        this.generateQuery()
        this.emitQuery()
      })

    this.resetFilterService
      .getFilterObservable()
      .pipe(
        takeUntil(this.end$),
        distinctUntilChanged(), // Add distinctUntilChanged here
        debounceTime(20),
        tap(() => {
          this.resetFilter()
          this.generateQuery()
          this.emitQuery()
        }),
      )
      .subscribe()

    // so... in the genericsearch filter component we will listen to changes from a specific transaction filter service
    // seems like a great idea

    if (this.transactionFilter) {
      this.filterService.activeTransactionIndexAsObs$.subscribe((index) => {
        // default to desc
        this.sortAscending = false
        // these need to be appended to the existing filters
        // but need to remove the previously emited filters from the service.. i.e. when we go from completed to errors -> we dont want to keep the completed filters
        if (this.transactionFilter) {
          this.removeCustomFilters()

          if (index === 'Error_Transactions') {
            this.addCustomFilter(
              ' +(disposition:FAILED OR terminalResult:"Not supported card" OR reason:"Failed") -state:CARD',
              'Errors',
            )
          } else if (index === 'Transactions') {
            this.addCustomFilter(
              ' -disposition:FAILED -state:CARD',
              'Completed Transactions',
            )
          } else if (index === 'Incomplete_Transactions') {
            this.addCustomFilter('', 'Incomplete Transactions')
          } else if (index === 'ALL_TRANSACTIONS') {
            this.addCustomFilter('', 'All Transactions')
          } else if (index === 'Card_read_only') {
            this.addCustomFilter(' +state:CARD', 'Card Reads')

            // this.onRemoveFilterByFilterType(this.options.filterFields.find((e) => e.field === 'disposition'));
          }

          setTimeout(() => {
            this.updateLocation(this.createRouteParams(this.filters))
            this.generateQuery()
            this.emitQuery()
            // make sure tags are correctly tracked by angular
          }, 0)
        }
      })
    }

    if (this.options) {
      if (this.options.filters) {
        for (const field in this.options.filters) {
          // add filters from options
          // console.log("adding filter from options (init?)", field, this.options.filters[field])
          this.addFilter(field, this.options.filters[field], null, true)
        }
        this.generateQuery()
        this.emitQuery()
      }

      if (!this.routeParams) {
        if (this.options.sort) {
          if (!this.routeParams) {
            const sortField = this.options.sort['field']
            this.sortField = this.findSortField(sortField)
            this.sortAscending =
              this?.sortField?.asc || this?.options?.sort['asc'] || false
          }
        } else {
          // else select first field...
          if (this.options.sortFields && this.options.sortFields.length > 0) {
            this.sortField = this.options.sortFields[0] // select first sort field by default
            this.sortAscending =
              this.options?.sort?.['asc'] === undefined ? true : false
          }
        }
        //  bad practise but it seems to work
        this.generateQuery()
        this.emitQuery()
      }
    }

    // initialize the forms
    this.form = this.fb.group({
      filterField: this.fb.control(null),
      filterType: this.fb.control(this.filterType),
      filterValue: this.fb.control(null),
      sortField: this.fb.control(this.sortField),
      search: this.fb.control(null),
    })

    // Set default filter
    if (this.options.filterFields && this.options.filterFields.length > 0) {
      this.form.controls.filterField.setValue(this.options.filterFields[0]) // select first filter field by default
    }

    this.filterTypes$ = combineLatest([
      this.defaultFilterTypes$,
      this.form.controls.filterField.valueChanges.pipe(
        startWith(this.form.controls.filterField.value),
      ),
    ]).pipe(
      map(([defaultFilterTypes, filterField]) => {
        return defaultFilterTypes.map((r) => {
          r.disabled = !filterField.filterTypes.includes(r.value)
          return r
        })
      }),
      tap((r) => {
        const selected = r.find(
          (item) => item.value === this.form.controls.filterType.value,
        )
        if (selected.disabled) {
          const item = r.find((item) => item.disabled === false)

          this.form.patchValue({
            filterType: item != null ? item.value : null,
          })
        }
      }),
      delay(0),
      tap((r) => {
        // to tick event
        if (this.form.get('filterType')?.value !== 'boolean') {
          this.form.controls.filterValue.setValue(null, {emitEvent: true})
        }
      }),
      tap((_) => this.form.markAsPristine()),
      share(),
    )

    this.form
      .get('filterValue')
      ?.valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(300),
        takeUntil(this.end$),
      )
      .subscribe((value) => {
        if (value === null) {
          return
        }
        const control = this.form.get('filterValue')
        if (!control?.errors) {
          if (this.listeningToFilterChanges) {
            this.onAddFilter()
          }
        } else {
          // if errors - check for empty -> if empty -> remove filter
          // should be typed instead of string checked
          if (control.errors.error === 'Cannot be empty') {
            const fieldToRemove =
              this.form.controls.filterField.value.field.toLowerCase()
            let indexFromFilters = -1
            this.filters.forEach((e, index) => {
              const lowerCasedField = e.field.field.toLowerCase()
              if (lowerCasedField === fieldToRemove) {
                indexFromFilters = index
              }
            })
            if (indexFromFilters !== -1) {
              this.onRemoveFilter(indexFromFilters)
            }
          }
        }
      })

    this.form.controls.sortField.valueChanges
      .pipe(takeUntil(this.end$))
      .subscribe((s) => {
        if (!this.sortFieldChanged) {
          this.sortAscending = s?.asc || false
          this.sortFieldChanged = true
        }
        this.onSort()
      })

    this.form.controls.search.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.end$))
      .subscribe((value) => {
        this.searchChanged(value)
      })

    this.form.controls.filterType.valueChanges
      .pipe(
        takeUntil(this.end$),
        startWith(this.form.controls.filterType.value),
        tap((v) => {
          this.toggleChangeDetection(false)
          const field = this.form.controls.filterField.value?.field
          if (field === 'managed') {
            const val = this.filters.find(
              (filter) => filter.field.field === field,
            )?.value
            this.activeFilterType$.next(filterTypeValue.BOOLEAN)
            this.form
              .get('filterValue')
              ?.setValue(val === 'false' ? false : true)
          } else {
            this.activeFilterType$.next(v)
          }

          setTimeout(() => {
            this.toggleChangeDetection(true)
          }, 500) // this should prevent the adding of date filters on selection of date filters..
          // only seems to work once ?
        }),
      )
      .subscribe(() => {})

    //made to trigger change domain
    if (this.total) {
      this.total.pipe(takeUntil(this.end$), debounceTime(40)).subscribe()
    }
  }

  toggleChangeDetection(state: boolean) {
    this.listeningToFilterChanges = state
  }

  private end$: Subject<void> = new Subject<void>()
  ngOnDestroy() {
    this.end$.next()
    this.end$.complete()
  }

  private findFilterField(field: string) {
    for (const f of this.options.filterFields) {
      if (f.field === field) {
        return f
      }
    }
    return null
  }
  private findSortField(field: string) {
    for (const f of this.options.sortFields) {
      if ((<FilterField>f).field === field) {
        return f
      }
    }
    return null
  }
  onAddFilter() {
    this.addFilter(
      this.form.controls.filterField.value,
      this.form.controls.filterValue.value,
      this.form.controls.filterType.value,
    )
    this.generateQuery()
    this.emitQuery()
    this.cdr.detectChanges()
  }
  onToggleSort() {
    this.toggleSort()

    this.generateQuery()
    this.emitQuery()
  }

  onToggleAdvanced() {
    this.isAdvanced = !this.isAdvanced

    /* Regenerate query from basic values */
    if (!this.isAdvanced) {
      this.updateLocation(this.createRouteParams(this.filters))
      this.generateQuery()
    } else {
      this.advancedForm.get('advanced')?.setValue(this.query)
    }
  }

  onAdvancedSearch() {
    this.generateQuery()
    this.emitQuery()
  }

  onRemoveFilter(idx: number) {
    if (idx === -1) {
      return
    }
    console.log('on remove filter', idx)
    this.options.filters = []

    this.removeFilter(idx)
    this.generateQuery()
    this.emitQuery()
  }

  onRemoveFilterByFilterType(flt: any) {
    console.log('on remove filter by type', flt)
    // removing by index was not very reliable as the filter fields are sorted somewhere
    const indexFromFilterArray = this.filters.findIndex(
      (obj) => obj.field.field === flt.field.field,
    )
    this.options.filters = []
    this.removeFilter(indexFromFilterArray)
    this.generateQuery()
    this.emitQuery()
  }

  onResetFilter() {
    this.toggleChangeDetection(false)
    this.searchValue = ''
    this.options.filters = []
    // this.form.get('filterValue').setValue(null,{emitEvent: false})
    this.form.get('search')?.setValue(null, {emitEvent: false})
    this.resetFilter()
    this.generateQuery()
    this.emitQuery()

    setTimeout(() => {
      this.toggleChangeDetection(true)
    }, 500) // this should prevent the adding of filters on reset... change detection hell
  }

  onSort() {
    this.sort()
    this.generateQuery()
    this.emitQuery()
  }

  addCustomFilter(
    query: string,
    description: string,
    removable: boolean = false,
  ) {
    this.filters.push({
      field: {
        field: description,
        label: description,
        filterTypes: [],
      },
      value: query,
      custom: true,
      notRemovable: !removable,
    })
    // this.updateLocation(this.createRouteParams(this.filters))
  }
  addFilter(
    field: FilterField | string,
    value: any,
    filterType = filterTypeValue.POSITIVE_MATCH,
    updateLocation: boolean = true,
  ) {
    // value is not a string - it is an any. It can be a string or an object or w/e. Removed the strict param.
    if (!value) {
      return
    }

    this.hasInvalidInput = false
    if (
      filterType === filterTypeValue.BEFORE ||
      filterType === filterTypeValue.AFTER ||
      filterType === filterTypeValue.PRIOR_TO
    ) {
      let numericValue: number | null = null
      let objectValue: number | null = null

      // Handle string input
      if (typeof value === 'string') {
        numericValue = parseFloat(value.trim()) // Convert without taking absolute value
      }
      // Handle object with amount property
      else if (
        typeof value === 'object' &&
        value !== null &&
        'amount' in value
      ) {
        objectValue = parseFloat(value.amount) // Convert without taking absolute value
      } else {
        this.hasInvalidInput = true
        this.invalidInputString = 'Search query is not valid'

        return // Invalid input type
      }

      // Check for NaN or absolute values exceeding 1,000,000
      if (
        (numericValue !== null &&
          (isNaN(numericValue) || Math.abs(numericValue) > 1000000)) ||
        (objectValue !== null &&
          (isNaN(objectValue) || Math.abs(objectValue) > 1000000))
      ) {
        this.hasInvalidInput = true
        this.invalidInputString =
          'Search query is higher then allowed maximum (1.000.000)'
        return // Return if invalid
      }
    }

    // trim value, if string
    if (value && typeof value === 'string') {
      value = value.trim()
      this.hasInvalidInput = value.includes('/')
      if (this.hasInvalidInput) {
        this.invalidInputString =
          'Search query string contains invalid character (/)'
        return
      }
    }
    const filter = {field: null, value: value, filterType: filterType}
    // find the filter field
    if (typeof field === 'string') {
      filter.field = this.findFilterField(field)
    } else {
      filter.field = field
    }
    if (typeof filter.value === 'string' && !filter.value.endsWith('*')) {
      filter.value += this.createPostFix(filter)
    }
    const idx = this.filters.findIndex((cur) => cur.field === filter.field)
    if (idx >= 0) {
      this.filters.splice(idx, 1)
    }
    // console.log("pushing filter: ", filter)
    this.filters.push(filter)

    if (updateLocation) {
      this.updateLocation(this.createRouteParams(this.filters))
    }

    setTimeout(() => {
      this.cdr.detectChanges()
      // make sure tags are correctly tracked by angular
    }, 0)
  }

  private parseRouteParams(params) {
    const filters = []
    this.routeQuery = []
    let sort = null
    let asc = true

    for (const key in params) {
      if (key === 'index') {
        // do nothing for now..
      } else if (key === 'adv') {
        this.setAdvanced(params[key])
      } else if (key === 'sort') {
        this.routeParams = true
        const field = this.findSortField(params[key])
        if (field) {
          sort = field
        }
      } else if (key === 'desc') {
        asc = false
      } else {
        const parsed = this.parseFilterFromKeyAndValue(key, params[key])
        const keyName = parsed.key

        const field = this.findFilterField(keyName)
        if (field) {
          this.routeParams = true
          filters.push({
            key: keyName,
            value: parsed.value,
            type: parsed.filterType,
          })
        } else {
          // store parameter to include in updates
          this.routeQuery.push(key + '=' + encodeURIComponent(params[key]))
        }
      }
    }

    if (this.routeParams) {
      this.filters = this.filters.filter((e) => e?.notRemovable)
      filters.forEach((filter) => {
        this.addFilter(filter.key, filter.value, filter.type)
      })
    }
    if (sort) {
      // if sort is set in the url param take make it leading
      this.setSort(sort, asc)
    } else {
      // else find the default
      this.setSort(
        this.sortField ? this.sortField : this.options.sortFields[0],
        asc,
      )
    }
  }

  private parseFilterFromKeyAndValue(key, value) {
    let filterType = filterTypeValue.POSITIVE_MATCH
    let keyName = key
    let val = value
    try {
      const parsedObject = JSON.parse(value)
      if (
        typeof parsedObject !== 'object' &&
        !parsedObject.hasOwnProperty('begin')
      ) {
        throw new Error()
      }
      val = parsedObject
      if (parsedObject.begin && parsedObject.end) {
        filterType = filterTypeValue.BETWEEN_TIMESTAMPS
      } else if (parsedObject.begin) {
        filterType = filterTypeValue.AFTER
      } else if (parsedObject.end) {
        filterType = filterTypeValue.BEFORE
      } else if (parsedObject.min && parsedObject.max) {
        filterType = filterTypeValue.BETWEEN_VALUES
      } else if (parsedObject.min) {
        filterType = filterTypeValue.GREATER_THAN
      } else if (parsedObject.max) {
        filterType = filterTypeValue.LESS_THAN
      } else if (parsedObject.timeUnit && parsedObject.amount) {
        if (parsedObject.amount < 0) {
          filterType = filterTypeValue.PRIOR_TO
        } else {
          filterType = filterTypeValue.SINCE
        }
      } else {
        filterType = null
      }
      return {key: keyName, filterType: filterType, value: val}
    } catch (e) {
      // it's a string or a boolean
      if (key.startsWith('!')) {
        filterType = filterTypeValue.NEGATIVE_MATCH
        if (typeof value === 'string' && !value.trim().endsWith('*')) {
          filterType = filterTypeValue.NEGATIVE_EXACT_MATCH
        }
        keyName = keyName.substr(1)
      } else {
        if (typeof value === 'string' && !value.trim().endsWith('*')) {
          filterType = filterTypeValue.POSITIVE_EXACT_MATCH
        }
      }
    }
    return {key: keyName, filterType: filterType, value: val}
  }

  private createRouteParams(filters: Filter[]) {
    const queryParts = filters.map((filter) => {
      if (filter.custom) {
        return 'index=' + filter.field.label.replace(' ', '') // cleaner url, will need extra mapping
      }
      const postfix =
        typeof filter.value === 'string' && filter.value.endsWith('*')
          ? ''
          : this.createPostFix(filter)
      let matchTypePrefix = ''
      if (
        filter.filterType &&
        [
          filterTypeValue.NEGATIVE_EXACT_MATCH,
          filterTypeValue.NEGATIVE_MATCH,
        ].includes(filter.filterType)
      ) {
        matchTypePrefix = '!'
      }
      let v = filter.value
      if (typeof filter.value === 'object') {
        v = JSON.stringify(v)
      }
      return `${matchTypePrefix}${filter.field.field}=${v}${postfix}`
    })
    if (this.sortField) {
      queryParts.push('sort=' + this.sortField.field)
    }
    if (!this.sortAscending) {
      queryParts.push('desc=true')
    }
    return queryParts
  }

  removeCustomFilters() {
    this.filters = this.filters.filter((e) => e.custom !== true)
  }

  removeFilter(idx: number) {
    // don remove filters that are notRemovable
    if (this.filters[idx]?.notRemovable) {
      return
    }
    this.filters.splice(idx, 1)
    this.updateLocation(this.createRouteParams(this.filters))
  }

  resetFilter() {
    this.filters = this.filters.filter((e) => e?.notRemovable)
    this.updateLocation(this.createRouteParams(this.filters))
  }

  searchChanged(value) {
    this.searchValue = value
  }

  toggleSort() {
    this.sortField = this.form.controls.sortField.value
    this.sortAscending = !this.sortAscending
    this.sortFieldChanged = false

    this.updateLocation(this.createRouteParams(this.filters))
  }

  setSort(sortField: FilterField | string, asc: boolean) {
    let matchField = null
    if (typeof sortField === 'string') {
      // match the field
      matchField = this.findSortField(sortField)
    } else {
      matchField = sortField
    }
    this.sortField = matchField
    if (!asc) {
      // field default
      asc = matchField?.asc ? matchField?.asc : true
    }
    this.sortAscending = asc

    if (this.form) {
      this.form.controls.sortField.setValue(this.sortField)
    }
  }

  sort() {
    this.sortField = this.form.controls.sortField.value
    this.sortAscending = this.sortField.asc ? !this.sortField.asc : false
    this.sortFieldChanged = true

    this.updateLocation(this.createRouteParams(this.filters))
  }

  setAdvanced(query: string) {
    this.isAdvanced = true
    this.advancedForm.get('advanced')?.setValue(query)
  }

  private updateLocation(query: string[], triggerQueryParamObs = false) {
    if (query.length === 0) {
      return
    }
    if (this.routeQuery) {
      // Check for duplicates
      const existingParams = new Set(this.routeQuery)
      const newParams = new Set(query)

      const hasDuplicates = Array.from(newParams).some((param) =>
        existingParams.has(param),
      )

      if (hasDuplicates) {
        return // Exit early if there are duplicates
      }

      let routeQuery = this.routeQuery.concat(query).join('&')

      // Merge logic for index parameter
      if (
        query.some((param) => param.includes('index=')) &&
        this.routeQuery.some((param) => param.includes('index='))
      ) {
        let filteredQuery = this.routeQuery.filter(
          (param) => !param.startsWith('index='),
        )
        routeQuery = filteredQuery.concat(query).join('&')
      } else {
        routeQuery = this.routeQuery.concat(query).join('&')
      }

      // Update the location
      this.location.replaceState(this.routeBase, routeQuery)

      // Trigger query parameter observable if needed
      if (triggerQueryParamObs) {
        const queryParams = this.parseQueryStringToParams(routeQuery)
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
          queryParamsHandling: 'merge', // or 'preserve' depending on your needs
        })
      }
    }
  }

  private parseQueryStringToParams(queryString: string): {[key: string]: any} {
    const params: {[key: string]: any} = {}
    queryString.split('&').forEach((param) => {
      const [key, value] = param.split('=')
      if (key) {
        params[decodeURIComponent(key)] = value
          ? decodeURIComponent(value)
          : null
      }
    })
    return params
  }
  private escapeValue(value: string): string {
    return value
      ? value
          .replace(' ', '\\ ')
          .replace(':', '\\:')
          .replace('+', '\\+')
          .replace('-', '\\-')
      : ''
  }

  generateQuery() {
    if (this.isAdvanced) {
      this.generateAdvancedQuery()
    } else {
      this.generateFilterQuery()
    }
  }

  onDelayedKeyUp() {}

  generateFilterQuery() {
    this.query = ''
    for (const flt of this.filters) {
      if (flt?.custom) {
        this.query += flt.value
      } else {
        // TODO : Create query builder service to escape special chars
        if (this.query && this.query !== '') {
          this.query += ' '
        }
        if (flt.value !== null && typeof flt.value === 'object') {
          this.query += ` ${this.createQueryFromObject(flt.value, flt.field.field)} `
        } else {
          const postfix =
            typeof flt.value === 'string' && flt.value.endsWith('*')
              ? ''
              : this.createPostFix(flt)
          this.query = this.query.concat(
            [
              filterTypeValue.NEGATIVE_MATCH,
              filterTypeValue.NEGATIVE_EXACT_MATCH,
            ].includes(flt.filterType)
              ? 'NOT '
              : '+',
            (<FilterField>flt.field).field,
            ':',
            this.escapeValue(flt.value.toString()),
            postfix,
          )
        }
      }
    }

    if (this.searchValue) {
      if (this.query && this.query !== '') {
        this.query += ' '
      }
      this.query = this.query.concat(
        '+*' + this.escapeValue(this.searchValue) + '*',
      )
    }
  }

  createPostFix(filter) {
    if (
      [filterTypeValue.NEGATIVE_MATCH, filterTypeValue.POSITIVE_MATCH].includes(
        filter.filterType,
      )
    ) {
      return '*'
    }
    return ''
  }

  isNegative(filter: Filter) {
    return [
      filterTypeValue.NEGATIVE_MATCH,
      filterTypeValue.NEGATIVE_EXACT_MATCH,
    ].includes(filter.filterType)
  }

  generateAdvancedQuery() {
    this.query = this.advancedForm.get('advanced')?.value

    const routeQuery = 'adv=' + encodeURIComponent(this.query)
    this.updateLocation([routeQuery])
  }

  emitQuery() {
    this.filterChange.emit({
      query: this.query,
      sort: this.sortField?.field,
      asc: this.sortAscending,
    })
  }

  fieldCompare = function (a, b) {
    return a?.field === b?.field
  }

  getPillValue(filter) {
    if (filter.value !== null && typeof filter.value === 'object') {
      return this.createPillFromObject(filter.value)
    } else {
      return filter.value
    }
  }

  createPillFromObject(val) {
    if (val.timeUnit && val.amount) {
      // it'a runtime range
      if (val.amount < 0) {
        return `prior to ${val.amount * -1} ${val.timeUnit} ago`
      }
      return `since ${val.amount} ${val.timeUnit}` // could handle single/multiple ie 1 day 2 days but doesn't for now
    }
    if (val.begin && val.end) {
      return `${moment(val.begin).format('DD-MM-YYYY HH:mm')} to ${moment(val.end).format('DD-MM-YYYY HH:mm')}`
    }
    if (val.begin && !val.end) {
      return `${moment(val.begin).format('DD-MM-YYYY HH:mm')} to now`
    }
    if (!val.begin && val.end) {
      return `before ${moment(val.end).format('DD-MM-YYYY HH:mm')}`
    }
    if (val.min !== null && val.max !== null) {
      return `${val.min.toString()} to ${val.max.toString()}`
    }
    if (val.min !== null && val.max === null) {
      return `from ${val.min.toString()}`
    }
    if (val.min === null && val.max !== null) {
      return `to ${val.max.toString()}`
    }
    return 'cannot parse filter'
  }

  createQueryFromObject(val, field) {
    if (val.timeUnit && val.amount) {
      if (parseInt(val.amount, 10) < 0) {
        return `+${field}:[0 TO ${moment().subtract(Math.abs(val.amount), val.timeUnit).valueOf()}]`
      }
      return `+${field}:[${moment().subtract(val.amount, val.timeUnit).valueOf()} TO *]`
    }
    if (val.begin || val.end) {
      return `+${field}:[${val.begin ? this.escapeValue(val.begin.toString()) : '*'} TO ${val.end ? this.escapeValue(val.end.toString()) : '*'}]`
    }
    if (val.min || val.max) {
      return `+${field}:[${val.min ? this.escapeValue(val.min.toString()) : '*'} TO ${val.max ? this.escapeValue(val.max.toString()) : '*'}]`
    }
    return ''
  }

  trackByFn(index: number, item: any): any {
    return item.field.field
  }
}
